
























































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate-3';

import { prettifyMessage } from '@/vee-validate';

import eventBus from '@/utils/eventBus';
import { treatValue } from '@/utils/moneyTreatments';
import SelectDropDownFieldUtils from '@/utils/selectDropdownFieldUtils';

import CoinsIcon from '@/components/icons/Coins.vue';
import TextField from '@/componentsV2/molecules/Inputs/TextField.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import InputWrapper from '@/componentsV2/molecules/Inputs/InputWrapper.vue';
import Typography from '@/componentsV2/atoms/Typography/Typography.vue';
import SelectDropdownField from '@/componentsV2/molecules/SelectDropdownField/SelectDropdownField.vue';

export enum EDealType {
  COIN_BUNDLE = 'coin-bundle',
  DUO_COIN_DEAL = 'duo-coin-deal',
  COIN_DEAL = 'coin-deal',
  FIRST_COIN_DEAL = 'first-coin-deal'
}

type FormDataType = {
  coins: number | null;
  value: number | null;
  twismCoin: number;
  merchantCoin: number;
  dealType: {
    value: EDealType;
    label: string;
  };
};

@Component({
  components: {
    ValidationObserver,
    ValidationProvider,
    TextField,
    InputWrapper,
    Typography,
    CoinsIcon,
    SelectDropdownField,
    FilledButton
  }
})
export default class DealForm extends Vue {
  @Prop() loading?: boolean;
  @Prop() sellCoinsBonus!: number;

  prettifyMessage = prettifyMessage;
  treatValue = treatValue;
  coinDealsQuantity = 1;

  $refs!: {
    slider: InstanceType<typeof HTMLInputElement>;
    observer: InstanceType<typeof ValidationObserver>;
  };

  key = 0;
  dealTypeSelectOptions = {
    ...SelectDropDownFieldUtils.getSelectdropdownPropertiesDefault(),
    searchable: false
  };
  private readonly defaultDealType = {
    value: EDealType.COIN_DEAL,
    label: 'Hot Deal'
  };
  private readonly dealTypeOptions = [
    { value: EDealType.COIN_DEAL, label: 'Hot Deal' },
    ...(this.sellCoinsBonus > 0
      ? [{ value: EDealType.DUO_COIN_DEAL, label: 'Duo Deal' }]
      : []),
    { value: EDealType.FIRST_COIN_DEAL, label: 'First Coin Deal' }
  ];

  form: FormDataType = {
    coins: null,
    value: null,
    twismCoin: 0,
    merchantCoin: 0,
    dealType: this.defaultDealType
  };

  get sumCoinsExceeds() {
    return (
      Number(this.form.twismCoin) + Number(this.form.merchantCoin) >
      Number(this.form.coins)
    );
  }

  get shouldShowDuoDealElements() {
    return (
      this.form.dealType &&
      this.form.dealType.value === EDealType.DUO_COIN_DEAL &&
      (this.form.coins || 0) >= 1
    );
  }

  get totalMerchantCoins() {
    return Number(this.form.merchantCoin) * this.coinDealsQuantity;
  }

  get totalTwismCoins() {
    return Number(this.form.twismCoin) * this.coinDealsQuantity;
  }

  get totalCoins() {
    return this.totalMerchantCoins + this.totalTwismCoins;
  }

  get minimumDealValue() {
    return this.form.dealType &&
      this.form.dealType.value === EDealType.FIRST_COIN_DEAL
      ? 2
      : 5;
  }

  get bonusAmountTotal() {
    return this.totalMerchantCoins * (this.sellCoinsBonus / 100);
  }

  get bonusAmount() {
    return (
      this.totalMerchantCoins -
      this.totalMerchantCoins /
        (1 + this.bonusAmountTotal / this.totalMerchantCoins)
    );
  }

  get merchantSellingValue() {
    return Math.max(0, this.totalMerchantCoins - this.bonusAmount);
  }

  get twismSellingValue() {
    const { value = 0 } = this.form;
    const merchantSelling = Number(this.merchantSellingValue) || 0;

    return Math.max(0, Number(value) - merchantSelling);
  }

  get merchantDiscountPercent() {
    return (1 - this.merchantSellingValue / this.totalMerchantCoins) * 100;
  }

  get twismDiscountPercent() {
    const { twismCoin = 0 } = this.form;

    const result = ((twismCoin - this.twismSellingValue) / twismCoin) * 100;

    return result % 1 === 0 ? result : Number(result.toFixed(2));
  }

  get consumerDiscountPercent() {
    const { value } = this.form;

    if (!value) return 0;

    return Math.floor((1 - value / this.totalCoins) * 100);
  }

  get merchantRevenueExceededPurchaseAmount() {
    const { value: purchaseAmount = 0 } = this.form;

    return this.merchantSellingValue > Number(purchaseAmount);
  }

  mounted() {
    eventBus.$on('resetDealForm', this.reset);
  }

  beforeUnmount() {
    eventBus.$off('resetDealForm', this.reset);
  }

  isValidValue(num: number) {
    return !isNaN(num);
  }

  reset() {
    const formRef = this.$refs.observer;

    formRef.reset();

    this.form = {
      dealType: this.defaultDealType,
      coins: null,
      value: null,
      twismCoin: 0,
      merchantCoin: 0
    };

    this.key++;
  }

  onCoinDistributionInput() {
    const { coins = 0, merchantCoin } = this.form;

    // Update twismCoin accordingly
    this.form.twismCoin = Math.max(1, Number(coins) - Number(merchantCoin));
  }

  calculateMaxMerchantCoins() {
    const { coins = 0, value: purchaseAmount = 0 } = this.form;
    const maxCoins = Number(coins);
    let currentMaxCoins = maxCoins;

    // Use iterative approach to find maximum merchant coin value that satisfies the constraint
    for (let i = 0; i < 3; i++) {
      // Calculate what the merchant selling value would be with the current max
      const merchantCoinQuantity = currentMaxCoins * this.coinDealsQuantity;
      const bonusTotal = merchantCoinQuantity * (this.sellCoinsBonus / 100);
      const bonus =
        merchantCoinQuantity -
        merchantCoinQuantity / (1 + bonusTotal / merchantCoinQuantity);
      const potentialMerchantSellingValue = Math.max(
        0,
        merchantCoinQuantity - bonus
      );

      // If potential merchant selling value exceeds purchase amount, reduce the max coins
      if (potentialMerchantSellingValue > Number(purchaseAmount)) {
        const ratio = Number(purchaseAmount) / potentialMerchantSellingValue;
        currentMaxCoins = Math.floor(currentMaxCoins * ratio);
      } else {
        // We've found a valid maximum
        break;
      }
    }

    return Math.min(maxCoins, Math.max(1, currentMaxCoins));
  }

  validateKeyDown(event: KeyboardEvent) {
    const char = event.key;
    const controlKeys = [
      'Backspace',
      'ArrowUp',
      'ArrowRight',
      'ArrowDown',
      'ArrowLeft',
      'Delete',
      'Tab'
    ];

    if (event.ctrlKey || event.metaKey) return;

    if (controlKeys.includes(char)) return;

    if (/^\d$/.test(char)) return;

    if (char === ',' && !String(this.form.merchantCoin).includes(',')) return;

    event.preventDefault();
  }

  handleMerchantInput(event: Event) {
    const { coins = 0, merchantCoin } = this.form;
    const value = Number((event.target as HTMLInputElement).value);

    this.form.merchantCoin = Math.min(value, Number(coins));
    this.form.twismCoin = Number(coins) - Number(merchantCoin);
  }

  handleTwismInput(event: Event) {
    const { coins = 0, twismCoin } = this.form;

    const value = Number((event.target as HTMLInputElement).value);

    this.form.twismCoin = Math.min(value, Number(coins));
    this.form.merchantCoin = Number(coins) - Number(twismCoin);
  }

  onCoinsAmountInput() {
    const { coins } = this.form;

    if (!coins) return;

    this.form.merchantCoin = coins / 2;
    this.form.twismCoin = coins / 2;
  }

  handleDealTypeChange() {
    if (
      this.form.dealType &&
      this.form.dealType.value === EDealType.DUO_COIN_DEAL
    ) {
      this.onCoinsAmountInput();
    }
  }

  async onSubmit() {
    const formRef = this.$refs.observer;
    const formIsValid = await formRef.validate();

    if (!formIsValid) return;

    if (
      this.form.dealType &&
      this.form.dealType.value === EDealType.DUO_COIN_DEAL &&
      this.sumCoinsExceeds
    ) {
      this.$notification(
        'The sum of Twism Coins and Merchant Coins cannot exceed the total Coins.',
        'error'
      );
      return;
    }

    const { coins, twismCoin, merchantCoin } = this.form;

    if (
      this.form.dealType &&
      this.form.dealType.value === EDealType.DUO_COIN_DEAL &&
      Number(twismCoin) + Number(merchantCoin) !== coins
    ) {
      this.$notification(
        'The sum of Twism Coins and Merchant Coins must equal the total Coins.',
        'error'
      );
      return;
    }

    if (
      this.form.dealType.value === EDealType.DUO_COIN_DEAL &&
      this.merchantRevenueExceededPurchaseAmount
    ) {
      const maxAllowedMerchantCoins = this.calculateMaxMerchantCoins();
      this.$notification(
        `The maximum allowed merchant coins is ${Math.floor(
          maxAllowedMerchantCoins
        )}.`,
        'error'
      );
      return;
    }

    const dealTypeValue = this.form.dealType ? this.form.dealType.value : null;

    this.$emit('submit', {
      ...this.form,
      dealType: dealTypeValue
    });
  }
}
